import { token } from '@atlaskit/tokens';

import { hexToRGBAValues } from './hex-to-rgba-values';

const getLuminance = ({ r, g, b }: { r: number; b: number; g: number }) => {
	const RsRGB = r / 255;
	const GsRGB = g / 255;
	const BsRGB = b / 255;

	const R = RsRGB <= 0.03928 ? RsRGB / 12.92 : Math.pow((RsRGB + 0.055) / 1.055, 2.4);
	const G = GsRGB <= 0.03928 ? GsRGB / 12.92 : Math.pow((GsRGB + 0.055) / 1.055, 2.4);
	const B = BsRGB <= 0.03928 ? BsRGB / 12.92 : Math.pow((BsRGB + 0.055) / 1.055, 2.4);

	return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};
const getRatio = (...luminance: [number, number]) => {
	const lighter = Math.max(...luminance);
	const darker = Math.min(...luminance);

	return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Returns an accessible hard-coded text color based on the color contrast with
 * the background.
 *
 * @param hex - The Hex color code of the background
 * @param [opts][.hardcodedSurface] - If set, a design token will be returned instead
 * of a hard-coded color. This is to support more transparent backgrounds
 * to allow the text to invert colors depending on the current theme's surface color.
 */
export const getTextColorForBackground: (
	hex: string,
	opts?: {
		hardcodedSurface?: 'light' | 'dark';
	},
) => string = (hex: string, opts?: { hardcodedSurface?: 'light' | 'dark' }): string => {
	const { r, g, b, a } = hexToRGBAValues(hex);
	const bgLum = getLuminance({ r, g, b });
	const alphaLimit = 0.5; // not sure where value comes from but this provides the proper outcome
	const blackRatio = getRatio(bgLum, 0); // 0 being the relative luminance of #000000
	const whiteRatio = getRatio(1, bgLum); // 1 being the relative luminance of #FFFFFF

	const alphaConditionsPerSurface: {
		light: boolean;
		dark: boolean;
	} = {
		light: a < alphaLimit,
		dark: a > alphaLimit,
	};

	const alphaLimitExceeded =
		opts?.hardcodedSurface && alphaConditionsPerSurface[opts.hardcodedSurface];

	if (!opts?.hardcodedSurface && a < alphaLimit) {
		// This color is transparent, so the text will mainly cast onto the surface behind.
		// Needs to use tokens otherwise Dark mode would cause black text on black surface
		return token('color.text');
	}

	return (blackRatio > whiteRatio && !a) || (a && alphaLimitExceeded) ? 'black' : 'white';
};
